import moment from 'moment';
import { Sensor, SensorGroup, SensorReadingType } from '@models';
import { Event, EventAction, SensorCondition, TimerCondition, MotionCondition, EventType, TimerEventType, TimingInterval, ActionType, AnalysisType, Comparator, LogicOperator } from '@models';
import { DashboardInfo } from '@models/Dashboard/dashboardInfo.model';
import { UserGroup } from '@models/User/usergroup.model';
import { UserAccessType } from '@models/User/UserAccessType.model';
import { TimeZoneDisplay } from '@models/Sensor/TimeZone.model';
import { ImageLabelInfo } from '@models/ImageFavorite/imageLabelInfo.model';
import { ImageSourceType } from '@models/ImageFavorite/imageFavorite.enums';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapLabelInfo } from '@models/MapFavorite/mapLabelInfo.model';

// The kind of thing the dashboard "Create" wizard builds. Only ChartReadings is
// enabled today; the rest are placeholders we will light up as we expand, each
// becoming its own per-type flow component (see the wizard container).
export enum WizardCreationType {
  ChartReadings = 'ChartReadings',
  GeoMapSensorsReadings = 'GeoMapSensorsReadings',
  ImageMapReadings = 'ImageMapReadings',
  DataTableReadings = 'DataTableReadings',
  TriggerConditionsActions = 'TriggerConditionsActions'
}

export interface WizardDateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

// Step-2 scope presets. They narrow which sensors/groups appear in the Sensors
// and Groups search list; they are not a selection themselves.
export interface SensorPresetFlags {
  recentlyAddedOrModified: boolean;
  assignedToMeOrGroup: boolean;
  allSensorsAndGroups: boolean;
}

// Everything the Chart Readings flow collects across steps 2-5. Save is deferred
// for this pass, but the shape is here so wiring Finish later is a fill-in.
export interface ChartFavoriteWizardModel {
  // step 2 - sensors
  selectedSensors: Sensor[];
  selectedSensorGroups: SensorGroup[];
  sensorPreset: SensorPresetFlags;

  // step 3 - readings
  availableReadingTypes: SensorReadingType[];
  selectedReadingTypes: SensorReadingType[];

  // step 4 - date range + display options (the echart owns the chart type)
  dateRange: WizardDateRange;
  dateRangeName: string;
  selectedTimeZone: TimeZoneDisplay;
  selectedTimeZoneOffset: number;
  showOriginalReadings: boolean;
  showCalibratedReadings: boolean;

  // step 5 - save (stubbed this pass)
  favoriteName: string;
  favoriteUserGroup: UserGroup;
  dashboardTargetMode: 'new' | 'existing';
  targetDashboard: DashboardInfo; // used when dashboardTargetMode === 'existing'
  newDashboardName: string;       // used when dashboardTargetMode === 'new'
  newDashboardUserGroup: UserGroup;
}

// The Notification & Action path builds a single sensor-based Event. The Event
// is the source of truth (shared by reference with the embedded event editor in
// the right pane); the extra fields are left-step UI scaffolding that writes into
// event.sensorConditions[0] / event.eventActions[0].
export type NotificationConditionPreset = 'ingestCount' | 'avg60Below' | 'minUnder' | null;

export interface NotificationWizardModel {
  event: Event;                              // built across steps; bound to the editor
  // step 2 - which triggering event (drives the step sequence + stepper count)
  triggerKind: 'sensorChange' | 'timer' | 'motion';
  // step 3 - sensors/group to watch (resolved to a sensor group at save)
  selectedSensors: Sensor[];
  selectedSensorGroups: SensorGroup[];
  sensorPreset: SensorPresetFlags;
  // step 4 - reading type to watch (single)
  availableReadingTypes: SensorReadingType[];
  // step 5 - what to watch for
  conditionPreset: NotificationConditionPreset;
  // step 7 - save (the name lives on event.name)
  accessUserGroup: UserGroup;
  dashboardTargetMode: 'new' | 'existing';
  targetDashboard: DashboardInfo;
  newDashboardName: string;
}

// Everything the Image Favorite flow collects across steps 2-5. Nothing is
// saved until Finish: labels are held here (new ones with negative temporary
// ids) and the right-pane preview renders them in draft mode.
export interface ImageFavoriteWizardModel {
  // step 2 - background source
  imageSourceType: ImageSourceType;
  // step 3 - static upload, or the dynamic gateway (+ optional media tag)
  file: File | null;
  fileDataUrl: string | null;
  existingStaticImageSrc: string | null;   // edit mode: the favorite's current static image
  gatewaySourceId: string | null;
  imageTag: string | null;
  previewImageSrc: string | null;          // what the right-pane preview shows
  // step 4 - labels (originalLabelIds lets an edit delete removed labels)
  labels: ImageLabelInfo[];
  originalLabelIds: number[];
  // step 5 - save
  favoriteName: string;
  favoriteUserGroup: UserGroup;
  dashboardTargetMode: 'new' | 'existing';
  targetDashboard: DashboardInfo;
  newDashboardName: string;
}

// Everything the Map Favorite flow collects across steps 2-4. Nothing is saved until
// Finish: labels are held here (new ones with negative temporary ids) and the
// right-pane preview map renders and edits them in draft mode.
export interface MapFavoriteWizardModel {
  // step 2 - sensors, reading types, date range and time zone (the shared selector)
  selectedSensors: Sensor[];
  selectedSensorGroups: SensorGroup[];
  availableReadingTypes: SensorReadingType[];
  selectedReadingTypes: SensorReadingType[];
  dateRange: WizardDateRange;
  dateRangeName: string;
  selectedTimeZone: TimeZoneDisplay;
  selectedTimeZoneOffset: number;
  // step 3 - the map view and open pin popups as last reported by the preview map,
  // and the labels (originalLabelIds lets an edit delete removed labels)
  mapDetails: MapDetails | null;
  labels: MapLabelInfo[];
  originalLabelIds: number[];
  // step 4 - save
  favoriteName: string;
  favoriteUserGroup: UserGroup;
  dashboardTargetMode: 'new' | 'existing';
  targetDashboard: DashboardInfo;
  newDashboardName: string;
}

export interface DashboardWizardState {
  creationType: WizardCreationType;
  step: number;                   // 1..6
  furthestStepReached: number;
  chartFavorite: ChartFavoriteWizardModel;
  notification: NotificationWizardModel;
  imageFavorite: ImageFavoriteWizardModel;
  mapFavorite: MapFavoriteWizardModel;
}

// Fresh state for a new wizard session. The date range defaults to "Last 3 Days"
// (matching DateRanges) until the user changes it in step 4.
export function createEmptyWizardState(): DashboardWizardState {
  return {
    creationType: null,
    step: 1,
    furthestStepReached: 1,
    chartFavorite: {
      selectedSensors: [],
      selectedSensorGroups: [],
      sensorPreset: { recentlyAddedOrModified: false, assignedToMeOrGroup: false, allSensorsAndGroups: true },
      availableReadingTypes: [],
      selectedReadingTypes: [],
      dateRange: { startDate: moment().subtract(2, 'days').startOf('day'), endDate: moment().endOf('day') },
      dateRangeName: 'Last 3 Days',
      selectedTimeZone: TimeZoneDisplay.Local,
      selectedTimeZoneOffset: moment().utcOffset(),
      showOriginalReadings: false,
      showCalibratedReadings: true,
      favoriteName: '',
      favoriteUserGroup: null,
      dashboardTargetMode: 'new',
      targetDashboard: null,
      newDashboardName: '',
      newDashboardUserGroup: null
    },
    notification: createEmptyNotificationState(),
    imageFavorite: createEmptyImageFavoriteState(),
    mapFavorite: createEmptyMapFavoriteState()
  };
}

export function createEmptyMapFavoriteState(): MapFavoriteWizardModel {
  return {
    selectedSensors: [],
    selectedSensorGroups: [],
    availableReadingTypes: [],
    selectedReadingTypes: [],
    dateRange: { startDate: moment().subtract(2, 'days').startOf('day'), endDate: moment().endOf('day') },
    dateRangeName: 'Last 3 Days',
    selectedTimeZone: TimeZoneDisplay.Local,
    selectedTimeZoneOffset: moment().utcOffset(),
    mapDetails: null,
    labels: [],
    originalLabelIds: [],
    favoriteName: '',
    favoriteUserGroup: null,
    dashboardTargetMode: 'new',
    targetDashboard: null,
    newDashboardName: ''
  };
}

export function createEmptyImageFavoriteState(): ImageFavoriteWizardModel {
  return {
    imageSourceType: ImageSourceType.Static,
    file: null,
    fileDataUrl: null,
    existingStaticImageSrc: null,
    gatewaySourceId: null,
    imageTag: null,
    previewImageSrc: null,
    labels: [],
    originalLabelIds: [],
    favoriteName: '',
    favoriteUserGroup: null,
    dashboardTargetMode: 'new',
    targetDashboard: null,
    newDashboardName: ''
  };
}

// Fresh Notification path state, seeded with a fully-shaped sensor-based Event
// (all condition/action arrays non-null, one default SensorCondition + one Email
// action, EditEvent granted) so the embedded event editor renders without throwing.
export function createEmptyNotificationState(): NotificationWizardModel {
  return {
    event: createNotificationEvent(),
    triggerKind: 'sensorChange',
    selectedSensors: [],
    selectedSensorGroups: [],
    sensorPreset: { recentlyAddedOrModified: false, assignedToMeOrGroup: false, allSensorsAndGroups: true },
    availableReadingTypes: [],
    conditionPreset: null,
    accessUserGroup: null,
    dashboardTargetMode: 'new',
    targetDashboard: null,
    newDashboardName: ''
  };
}

export function createNotificationEvent(): Event {
  const now = new Date();
  return {
    id: 0,
    name: '',
    description: '',
    userName: '',
    userGroup: '',
    userGroupID: null,
    userAccess: UserAccessType.ReadWrite,
    eventType: EventType.SensorBased,
    sensorConditions: [createDefaultSensorCondition()],
    timerConditions: [],
    modelConditions: [],
    motionConditions: [],
    eventActions: [createDefaultEmailAction()],
    isDisabled: false,
    createdDate: now,
    modifiedDate: now,
    ignoreUntilDate: now,
    evaluateStartTime: '00:15',
    evaluateEndTime: '23:45',
    evaluateDaysOfWeek: [true, true, true, true, true, true, true],
    checked: false,
    overallActionStatus: { state: 0, duration: 0 },
    useActionQueue: false,
    actionQueueId: 0,
    userFeatureAccess: { EditEvent: true, DeleteEvent: true, ViewEvent: true }
  } as Event;
}

export function createDefaultSensorCondition(): SensorCondition {
  return {
    id: 0,
    eventID: 0,
    sensorGroupID: undefined,
    sensorGroup: undefined,
    sensorReadingType: undefined,
    numberOfSamples: 0,
    lastXMinutes: 0,
    analysisType: AnalysisType.median,
    comparator: Comparator.lt,
    threshold: 0,
    logicOperator: LogicOperator.And,
    order: 0
  };
}

// Mirrors the Events editor's newTimerCondition() default (timingSingle must be an
// HH:15/HH:45 string to match the editor's time dropdown).
export function createDefaultTimerCondition(eventId: number): TimerCondition {
  return {
    id: 0,
    eventID: eventId,
    type: TimerEventType.Single,
    daysOfWeek: [false, false, false, false, false, false, false],
    timingSingle: '08:15',
    timingRepeat: TimingInterval.EveryTwoHours,
    logicOperator: LogicOperator.And,
    order: 0
  };
}

// Mirrors the Events editor's newMotionCondition() default.
export function createDefaultMotionCondition(eventId: number): MotionCondition {
  return {
    id: 0,
    eventID: eventId,
    deviceSerial: '',
    deviceName: '',
    sensitivity: 80,
    detectionArea: 'Full Image',
    postMotionSleepTime: 60,
    preMotionCaptureTime: 0,
    logicOperator: LogicOperator.And,
    order: 0
  };
}

export function createDefaultEmailAction(): EventAction {
  return {
    id: 0,
    eventID: 0,
    type: ActionType.Email,
    destination: '',
    deviceName: '',
    deviceSerial: '',
    zone: 20,
    scheduleName: '',
    scheduleID: -1,
    taskAction: '',
    onError: '',
    mediaResolution: 'Low',
    mediaDuration: 5,
    mediaDelay: 0,
    cameraId: 0,
    cameraName: '',
    cameraModel: '',
    cameraPresets: '',
    mediaActionOptions: [],
    sourceZone: 21,
    fillZone: 22,
    fillScheduleName: '',
    fillScheduleId: -1,
    drainZone: 23,
    drainScheduleName: '',
    drainScheduleId: -1,
    irrigationActionOptions: undefined,
    notificationSectionOptions: undefined
  } as EventAction;
}
